"use client";

import React, { ReactNode, useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import { X } from "lucide-react";
import { cn } from "utils/utils";
import { ICON_SIZE } from "modules/common/components/navigation/Menu";

type ModalProps = {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  children: ReactNode;
  actions?: ReactNode;
  maxWidth?:
    | "none"
    | "sm"
    | "md"
    | "lg"
    | "xl"
    | "2xl"
    | "3xl"
    | "4xl"
    | "5xl"
    | "6xl"
    | "7xl";
  className?: string;
  containerClassName?: string;
  height?: "auto" | "full";
  showCloseButton?: boolean;
  placement?: "top" | "middle" | "bottom" | "start" | "end";
  allowOverflow?: boolean;
  closeOnNavigate?: boolean;
  /** Blocks ESC, backdrop clicks and the close button, e.g. mid-payment. */
  preventClose?: boolean;
  /** Replaces the content wrapper's default padding classes. */
  bodyClassName?: string;
  // Deprecated FAB trigger props - kept for backwards compatibility but no longer rendered
  /** @deprecated FABs have been removed. Use inline buttons instead. */
  triggerButton?: ReactNode;
  /** @deprecated FABs have been removed. Use inline buttons instead. */
  triggerIcon?: ReactNode;
  /** @deprecated FABs have been removed. */
  triggerPosition?: "left" | "center" | "right";
  /** @deprecated FABs have been removed. */
  bottomClass?: string;
  /** @deprecated FABs have been removed. */
  triggerClassName?: string;
};

const maxWidthClasses = {
  none: "max-w-none",
  sm: "md:max-w-sm",
  md: "md:max-w-md",
  lg: "md:max-w-lg",
  xl: "md:max-w-xl",
  "2xl": "md:max-w-2xl",
  "3xl": "md:max-w-3xl",
  "4xl": "md:max-w-4xl",
  "5xl": "md:max-w-5xl",
  "6xl": "md:max-w-6xl",
  "7xl": "md:max-w-7xl",
} as const;

const heightClasses = {
  auto: "h-auto md:h-auto",
  full: "h-[90vh] md:max-h-[90vh]",
} as const;

const placementClasses = {
  top: "modal-top",
  middle: "modal-middle",
  bottom: "modal-bottom",
  start: "modal-start",
  end: "modal-end",
} as const;

export function Modal({
  open,
  onClose,
  title,
  children,
  actions,
  maxWidth = "2xl",
  className,
  containerClassName,
  height = "auto",
  showCloseButton = true,
  placement = "middle",
  allowOverflow = false,
  closeOnNavigate = true,
  preventClose = false,
  bodyClassName,
}: ModalProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const pathname = usePathname();
  const openPathnameRef = useRef<string | null>(null);

  useEffect(() => {
    if (open) {
      openPathnameRef.current = pathname;
    } else {
      openPathnameRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (
      closeOnNavigate &&
      open &&
      openPathnameRef.current !== null &&
      pathname !== openPathnameRef.current
    ) {
      onClose();
    }
  }, [pathname, closeOnNavigate, open, onClose]);

  // Handle ESC key press
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && open) {
        event.preventDefault();
        if (!preventClose) onClose();
      }
    };

    if (open) {
      document.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, onClose, preventClose]);

  // Handle dialog open/close with better animation timing
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (open) {
      // Small delay to ensure proper animation timing
      const timeoutId = setTimeout(() => {
        if (!dialog.open) {
          // showModal() breaks the layering of toasts https://stackoverflow.com/a/77114563
          // dialog.showModal();
          dialog.showModal();
        }
      }, 0);
      return () => clearTimeout(timeoutId);
    } else {
      if (dialog.open) {
        dialog.close();
      }
    }
  }, [open]);

  // Handle dialog close events
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    dialog.addEventListener("close", onClose);

    return () => {
      dialog.removeEventListener("close", onClose);
    };
  }, [onClose]);

  // Handle click outside modal content to close
  // const handleDialogClick = (e: React.MouseEvent) => {
  //   const target = e.target as HTMLElement;
  //   // Close if clicking directly on the dialog element (backdrop area)
  //   if (target === dialogRef.current) {
  //     onClose();
  //   }
  // };

  return (
    <>
      <dialog
        ref={dialogRef}
        className={cn(
          "modal",
          // Keep mobile bottom behavior for better UX unless explicitly overridden
          placement === "middle"
            ? "modal-bottom sm:modal-middle"
            : placementClasses[placement],
          containerClassName
        )}
        // onClick={handleDialogClick}
        onCancel={(e) => {
          e.preventDefault();
          if (!preventClose) onClose();
        }}
      >
        <div
          className={cn(
            "modal-box relative flex flex-col p-0",
            "sm:rounded-box rounded-t-box w-full rounded-b-none",
            heightClasses[height],
            maxWidthClasses[maxWidth],
            allowOverflow
              ? "overflow-visible"
              : height === "full"
                ? "overflow-hidden"
                : "overflow-auto",
            className
          )}
        >
          {title && (
            <div className="border-base-200 bg-base-100 rounded-t-box sticky top-0 z-30 flex flex-col pt-[env(safe-area-inset-top)]">
              <div className="bg-base-content/20 mx-auto mb-1 mt-2 h-1 w-10 rounded-full sm:hidden" />
              <div className="border-base-200 inline-flex items-center justify-between border-b px-4 py-3 md:px-6 md:py-4">
                {typeof title === "string" ? (
                  <h2 className="text-base-content text-xl font-semibold md:text-2xl">
                    {title}
                  </h2>
                ) : (
                  title
                )}
                {showCloseButton && (
                  <form method="dialog">
                    <button
                      className="btn btn-circle btn-ghost btn-sm"
                      aria-label="Close modal"
                      disabled={preventClose}
                    >
                      <X size={ICON_SIZE} />
                    </button>
                  </form>
                )}
              </div>
            </div>
          )}

          <div
            className={cn(
              "flex min-h-0 flex-1 flex-col",
              allowOverflow ? "overflow-visible" : "overflow-auto",
              bodyClassName ??
                "p-4 pb-[max(1rem,env(safe-area-inset-bottom))] md:px-6 md:py-4 md:pt-4"
            )}
          >
            {children}
          </div>

          {actions && (
            <div className="border-base-200 bg-base-100 sticky bottom-0 z-30 border-t px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] md:px-6 md:py-4">
              <form method="dialog" className="flex justify-end gap-2">
                {actions}
              </form>
            </div>
          )}
        </div>
        {/* Programmatic backdrop click handler to prevent phantom clicks from mobile select pickers */}
        <div
          className="modal-backdrop"
          onClick={(e) => {
            // Only close if clicking directly on the backdrop, not on content
            if (e.target === e.currentTarget && !preventClose) {
              // Use native dialog close to preserve CSS animations
              // The 'close' event listener will call onClose() to sync React state
              dialogRef.current?.close();
            }
          }}
        />
      </dialog>
    </>
  );
}
